/**
 * ESS Climate Intelligence — RONI + Niño 3.4 Historical Chart
 * ECharts dual-line chart with period selector and threshold markers
 */

import * as echarts from 'echarts';
import { fetchRONIData, filterByYears } from '../data/noaa.js';
import { infoTrigger, infoPanel } from './InfoNote.js';
import { whenInViewport } from '../utils/viewport.js';

export function createRONIChart() {
  const section = document.createElement('section');
  section.id = 'roni-chart';
  section.className = 'section';
  section.style.background = 'linear-gradient(180deg, var(--clr-void) 0%, var(--clr-deep) 100%)';
  section.innerHTML = `
    <div class="container">
      <div class="section-header anim-on-scroll">
        <div class="section-header__eyebrow">Section 02</div>
        <h2 class="section-header__title">How Strong Is This El Niño? ${infoTrigger('roni-chart')}</h2>
        <p class="section-header__subtitle">
          RONI (Relative Oceanic Niño Index) is NOAA's official El Niño monitoring metric.
          a 3-month running mean that removes the long-term warming trend, making events
          comparable across decades. This chart plots RONI alongside the Niño 3.4 SST anomaly
          (both in °C anomaly terms), and lets you compare the current event with previous ones.
        </p>
      </div>

      ${infoPanel('roni-chart', {
    title: 'Reading This Chart',
    meaning: 'Higher positive values indicate stronger El Niño warming. Negative values point toward La Niña conditions. Both lines show departures from the usual temperature, not absolute ocean temperatures.',
    implication: 'Strong Pacific warming can increase the likelihood of climate effects in some regions, but the same ENSO strength does not necessarily produce the same agricultural conditions every time.',
    takeaway: 'This graph tells us how unusual the current global climate signal is compared with history. It does not by itself tell us the severity of agricultural impacts. Historical comparison helps us understand whether the current Pacific signal is weak, moderate or unusually strong relative to previous events.',
  })}

      <div class="roni-chart-wrap glass-card anim-on-scroll">
        <div class="roni-chart-header">
          <div class="roni-chart-header__left">
            <div class="roni-legends">
              <div class="roni-legend-item">
                <span class="roni-legend-dot" style="background:#f97316"></span>
                RONI (Official)
              </div>
              <div class="roni-legend-item">
                <span class="roni-legend-dot" style="background:#8dc53d"></span>
                Niño 3.4 SST Anomaly
              </div>
            </div>
          </div>
          <div class="roni-chart-header__right">
            <div class="period-tabs" id="roniPeriodTabs">
              <button class="period-tab" data-years="5">5 Years</button>
              <button class="period-tab" data-years="20">20 Years</button>
              <button class="period-tab active" data-years="all">Full Record</button>
            </div>
          </div>
        </div>

        <div class="roni-chart-thresholds">
          <span class="threshold-tag threshold-tag--warm">El Niño ≥ +0.5°C</span>
          <span class="threshold-tag threshold-tag--cool">La Niña ≤ −0.5°C</span>
        </div>

        <div id="roniChartEl" class="roni-chart-canvas"></div>

        <div class="roni-chart-footer">
          <span class="source-note">Source: <a href="https://www.cpc.ncep.noaa.gov" target="_blank">NOAA Climate Prediction Center</a> · RONI updated monthly by the 5th · Recent values subject to revision</span>
        </div>
      </div>
    </div>
  `;

  injectRONIStyles();

  // Wait for DOM, then init chart
  requestAnimationFrame(() => initRONIChart(section));
  return section;
}

async function initRONIChart(section) {
  const el = section.querySelector('#roniChartEl');
  if (!el) return;

  const allData = await fetchRONIData();

  await whenInViewport(el);

  const chart = echarts.init(el, null, {
    renderer: 'canvas',
    useDirtyRect: true,
  });

  let currentYears = 'all';

  function renderChart(years) {
    const data = filterByYears(allData, years);
    const dates = data.map(d => d.label);
    const roni = data.map(d => d.roni);
    const nino34 = data.map(d => d.nino34);

    // Create visual pieces for RONI fill colors
    const markAreas = [];
    // For simplicity use areaStyle with split by threshold

    chart.setOption({
      animation: true,
      animationDuration: 1500,
      animationEasing: 'cubicOut',
      animationDelay: index => Math.min(index * 8, 650),
      backgroundColor: 'transparent',
      grid: { top: 24, left: 60, right: 24, bottom: 56 },
      tooltip: {
        trigger: 'axis',
        backgroundColor: 'rgba(18,27,53,0.95)',
        borderColor: 'rgba(141,197,61,0.3)',
        borderWidth: 1,
        padding: [10, 14],
        extraCssText: 'box-shadow: 0 8px 24px rgba(0,0,0,0.4); border-radius: 10px;',
        textStyle: { color: '#f1f5f9', fontFamily: 'Inter, sans-serif', fontSize: 13 },
        axisPointer: {
          type: 'line',
          lineStyle: { color: 'rgba(141,197,61,0.5)', width: 1.5, type: 'dashed' },
          label: {
            backgroundColor: '#0f766e',
            color: '#f1f5f9',
            fontFamily: 'JetBrains Mono, monospace',
            fontSize: 11,
          },
        },
        formatter(params) {
          const label = params[0].axisValue;
          const rows = params.map(p => {
            const color = p.seriesIndex === 0 ? '#f97316' : '#8dc53d';
            const val = p.value;
            // Niño 3.4 has no data before ~1982 (NOAA's sstoi.indices doesn't
            // go back as far as RONI's own record) — render those points as
            // "No data" instead of crashing the formatter on val.toFixed().
            if (val === null || val === undefined || Number.isNaN(val)) {
              return `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
                <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color};opacity:0.4"></span>
                <span style="color:#94a3b8">${p.seriesName}</span>
                <span style="margin-left:auto;color:#64748b">No data</span>
              </div>`;
            }
            const sign = val >= 0 ? '+' : '';
            return `<div style="display:flex;align-items:center;gap:8px;margin-top:4px;">
              <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${color}"></span>
              <span style="color:#94a3b8">${p.seriesName}</span>
              <span style="margin-left:auto;font-weight:600;color:${val >= 0.5 ? '#f97316' : val <= -0.5 ? '#38bdf8' : '#f1f5f9'}">${sign}${val.toFixed(2)}°C</span>
            </div>`;
          }).join('');
          return `<div style="font-size:12px;font-family:'JetBrains Mono',monospace;color:#94a3b8;margin-bottom:4px">${label}</div>${rows}`;
        },
      },
      xAxis: {
        type: 'category',
        data: dates,
        axisLine: { lineStyle: { color: 'rgba(255,255,255,0.1)' } },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(255,255,255,0.4)',
          fontSize: 11,
          fontFamily: 'Inter, sans-serif',
          rotate: dates.length > 60 ? 45 : 0,
          interval: Math.floor(dates.length / 12),
        },
        splitLine: { show: false },
      },
      yAxis: {
        type: 'value',
        name: '°C',
        nameTextStyle: { color: 'rgba(255,255,255,0.4)', fontSize: 11 },
        axisLine: { show: false },
        axisTick: { show: false },
        axisLabel: {
          color: 'rgba(255,255,255,0.4)',
          fontSize: 11,
          fontFamily: 'JetBrains Mono, monospace',
          formatter: v => `${v >= 0 ? '+' : ''}${v.toFixed(1)}`,
        },
        splitLine: { lineStyle: { color: 'rgba(255,255,255,0.05)', type: 'solid' } },
      },
      markLine: {},
      series: [
        {
          name: 'RONI',
          type: 'line',
          data: roni,
          smooth: 0.3,
          symbol: 'circle',
          symbolSize: 7,
          itemStyle: { color: '#f97316', opacity: 0 },
          lineStyle: { color: '#f97316', width: 2.5 },
          areaStyle: {
            color: {
              type: 'linear', x: 0, y: 0, x2: 0, y2: 1,
              colorStops: [
                { offset: 0, color: 'rgba(249,115,22,0.25)' },
                { offset: 1, color: 'rgba(249,115,22,0)' },
              ],
            },
          },
          emphasis: {
            focus: 'series',
            scale: 1.6,
            lineStyle: { width: 3.5 },
            itemStyle: { color: '#f97316', opacity: 1, borderWidth: 2, borderColor: '#fff', shadowBlur: 12, shadowColor: 'rgba(249,115,22,0.7)' },
          },
          blur: { lineStyle: { opacity: 0.25 }, areaStyle: { opacity: 0.08 } },
          markLine: {
            symbol: 'none',
            silent: true,
            lineStyle: { type: 'dashed', color: 'rgba(249,115,22,0.4)', width: 1 },
            data: [
              { yAxis: 0.5, label: { show: false } },
              { yAxis: -0.5, label: { show: false } },
            ],
          },
        },
        {
          name: 'Niño 3.4',
          type: 'line',
          data: nino34,
          smooth: 0.3,
          symbol: 'circle',
          symbolSize: 6,
          itemStyle: { color: '#8dc53d', opacity: 0 },
          lineStyle: { color: '#8dc53d', width: 1.5, opacity: 0.7 },
          emphasis: {
            focus: 'series',
            scale: 1.6,
            lineStyle: { width: 2.5, opacity: 1 },
            itemStyle: { color: '#8dc53d', opacity: 1, borderWidth: 2, borderColor: '#fff', shadowBlur: 12, shadowColor: 'rgba(141,197,61,0.7)' },
          },
          blur: { lineStyle: { opacity: 0.12 } },
        },
      ],
    }, true);

    // Highlight current value
    // The newest Niño 3.4 month can legitimately be newer than the latest
    // three-month RONI season, so annotate the latest record that has RONI.
    const latest = allData.slice().reverse().find(record => record.roni != null);
    if (latest) {
      chart.setOption({
        graphic: [{
          type: 'group',
          right: 24,
          top: 24,
          children: [
            {
              type: 'rect',
              z: 10,
              shape: { x: 0, y: 0, width: 120, height: 52, r: 6 },
              style: { fill: 'rgba(249,115,22,0.15)', stroke: 'rgba(249,115,22,0.3)', lineWidth: 1 },
            },
            {
              type: 'text',
              z: 11,
              style: {
                text: `CURRENT\nRONI +${latest.roni.toFixed(2)}°C`,
                fill: '#f97316',
                fontSize: 11,
                fontFamily: 'JetBrains Mono, monospace',
                fontWeight: 'bold',
                x: 60,
                y: 26,
                textAlign: 'center',
                textVerticalAlign: 'middle',
                lineHeight: 16,
              },
            },
          ],
        }],
      });
    }
  }

  renderChart('all');

  // Period tab handler
  section.querySelector('#roniPeriodTabs').addEventListener('click', (e) => {
    const btn = e.target.closest('.period-tab');
    if (!btn) return;
    const years = btn.dataset.years === 'all' ? 'all' : parseInt(btn.dataset.years);
    section.querySelectorAll('.period-tab').forEach(b => b.classList.toggle('active', b === btn));
    currentYears = years;
    renderChart(years);
  });

  // Resize — use ResizeObserver so chart responds to container changes too
  const ro = new ResizeObserver(() => chart.resize());
  ro.observe(el);
  window.addEventListener('resize', () => chart.resize());
}

function injectRONIStyles() {
  if (document.getElementById('roni-styles')) return;
  const style = document.createElement('style');
  style.id = 'roni-styles';
  style.textContent = `
    .roni-chart-wrap {
      padding: var(--space-6);
    }

    .roni-chart-canvas {
      width: 100%;
      height: 360px;
    }

    @media (max-width: 768px) {
      .roni-chart-canvas { height: 280px; }
    }
    @media (max-width: 480px) {
      .roni-chart-canvas { height: 220px; }
    }

    .roni-chart-header {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: var(--space-4);
      margin-bottom: var(--space-4);
    }

    .roni-legends {
      display: flex;
      gap: var(--space-6);
    }

    .roni-legend-item {
      display: flex;
      align-items: center;
      gap: var(--space-2);
      font-size: var(--text-sm);
      color: var(--clr-text-secondary);
    }

    .roni-legend-dot {
      width: 10px;
      height: 10px;
      border-radius: 50%;
      flex-shrink: 0;
    }

    .roni-chart-thresholds {
      display: flex;
      gap: var(--space-3);
      margin-bottom: var(--space-3);
    }

    .threshold-tag {
      font-family: var(--font-mono);
      font-size: 10px;
      padding: 2px 8px;
      border-radius: var(--radius-full);
      border: 1px solid;
    }

    .threshold-tag--warm {
      color: var(--clr-el-nino);
      border-color: rgba(249,115,22,0.3);
      background: rgba(249,115,22,0.08);
    }

    .threshold-tag--cool {
      color: var(--clr-la-nina);
      border-color: rgba(56,189,248,0.3);
      background: rgba(56,189,248,0.08);
    }

    .roni-chart-footer {
      margin-top: var(--space-4);
      padding-top: var(--space-4);
      border-top: 1px solid var(--clr-border);
    }
  `;
  document.head.appendChild(style);
}
